'use client';

import { c as _c } from "react/compiler-runtime";
import { ChartTooltipPanel, FloatingTooltip } from '@/domains/pages/shared/chart-tooltip';
import { MODEL_ID_COLORS, MODEL_ORDER, MODEL_TIERS, MODEL_TIER_LABELS, MODEL_TIER_TOOLTIPS } from '@/domains/pages/model-savings/constants';
import type { ModelId, ModelMixSegment } from '@/domains/pages/model-savings/types';
import { mixChangePoints } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { formatScore } from '@/utils/format.util';
import { InfoTooltip } from '@/comps/info-tooltip';
import { cn } from '@/utils/shad';
import { useCallback, useRef, useState } from 'react';
type ModelMixBarsProps = {
  actualMix: ModelMixSegment[];
  optimalMix: ModelMixSegment[];
  compact?: boolean;
  showLabels?: boolean;
  className?: string;
};
type HoverState = {
  modelId: ModelId;
  bar: 'actual' | 'optimal';
  x: number;
  y: number;
};
function getMixPercent(mix: ModelMixSegment[], modelId: ModelId): number {
  return mix.find(segment => segment.modelId === modelId)?.percent ?? 0;
}
function StackedBar(t0) {
  const $ = _c(11);
  const {
    mix,
    barKey,
    hoveredModelId,
    compact,
    onSegmentHover,
    onSegmentLeave
  } = t0;
  const t1 = compact ? "h-2.5" : "h-3.5";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex w-full overflow-hidden", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== barKey || $[3] !== hoveredModelId || $[4] !== mix || $[5] !== onSegmentHover) {
    t3 = MODEL_ORDER.map(modelId => {
      const percent = getMixPercent(mix, modelId);
      if (percent <= 0) {
        return null;
      }
      const isDimmed = hoveredModelId != null && hoveredModelId !== modelId;
      const color = MODEL_ID_COLORS[modelId];
      return <div key={`${barKey}-${modelId}`} className="h-full transition-opacity" style={{
        width: `${percent}%`,
        opacity: isDimmed ? 0.25 : 1,
        border: `1px solid ${color}`,
        backgroundColor: `${color}18`,
        boxSizing: "border-box"
      }} onMouseEnter={event => onSegmentHover(modelId, event)} onMouseMove={event_0 => onSegmentHover(modelId, event_0)} />;
    });
    $[2] = barKey;
    $[3] = hoveredModelId;
    $[4] = mix;
    $[5] = onSegmentHover;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== onSegmentLeave || $[8] !== t2 || $[9] !== t3) {
    t4 = <div className={t2} onMouseLeave={onSegmentLeave}>{t3}</div>;
    $[7] = onSegmentLeave;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
export function ModelMixBars(t0) {
  const $ = _c(32);
  const {
    actualMix,
    optimalMix,
    compact: t1,
    showLabels: t2,
    className
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const showLabels = t2 === undefined ? false : t2;
  const containerRef = useRef(null);
  const [hover, setHover] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = (modelId, bar, event) => {
      setHover({
        modelId,
        bar,
        x: event.clientX,
        y: event.clientY
      });
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const handleSegmentHover = t3;
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => setHover(null);
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const handleLeave = t4;
  const hoveredModelId = hover?.modelId ?? null;
  let t5;
  if ($[2] !== actualMix || $[3] !== className || $[4] !== compact || $[5] !== hover || $[6] !== hoveredModelId || $[7] !== optimalMix || $[8] !== showLabels) {
    const changePts = hoveredModelId ? mixChangePoints(actualMix, optimalMix, hoveredModelId) : 0;
    const actualPercent = hoveredModelId ? getMixPercent(actualMix, hoveredModelId) : 0;
    const optimalPercent = hoveredModelId ? getMixPercent(optimalMix, hoveredModelId) : 0;
    let t6;
    if ($[10] !== className) {
      t6 = cn("flex flex-col gap-1.5", className);
      $[10] = className;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== showLabels) {
      t7 = showLabels ? <span className="text-[10px] text-muted-foreground">Actual</span> : <span />;
      $[12] = showLabels;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = (modelId_0, event_0) => handleSegmentHover(modelId_0, "actual", event_0);
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    let t9;
    if ($[15] !== actualMix || $[16] !== compact || $[17] !== hoveredModelId) {
      t9 = <StackedBar mix={actualMix} barKey="actual" hoveredModelId={hoveredModelId} compact={compact} onSegmentHover={t8} onSegmentLeave={handleLeave} />;
      $[15] = actualMix;
      $[16] = compact;
      $[17] = hoveredModelId;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[19] !== t7 || $[20] !== t9) {
      t10 = <div className="grid grid-cols-[44px_1fr] items-center gap-2">{t7}{t9}</div>;
      $[19] = t7;
      $[20] = t9;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== showLabels) {
      t11 = showLabels ? <span className="text-[10px] text-muted-foreground">Optimal</span> : <span />;
      $[22] = showLabels;
      $[23] = t11;
    } else {
      t11 = $[23];
    }
    let t12;
    if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = (modelId_1, event_1) => handleSegmentHover(modelId_1, "optimal", event_1);
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    let t13;
    if ($[25] !== compact || $[26] !== hoveredModelId || $[27] !== optimalMix) {
      t13 = <StackedBar mix={optimalMix} barKey="optimal" hoveredModelId={hoveredModelId} compact={compact} onSegmentHover={t12} onSegmentLeave={handleLeave} />;
      $[25] = compact;
      $[26] = hoveredModelId;
      $[27] = optimalMix;
      $[28] = t13;
    } else {
      t13 = $[28];
    }
    let t14;
    if ($[29] !== t11 || $[30] !== t13) {
      t14 = <div className="grid grid-cols-[44px_1fr] items-center gap-2">{t11}{t13}</div>;
      $[29] = t11;
      $[30] = t13;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    t5 = <div ref={containerRef} className={t6}>{t10}{t14}{hover ? <FloatingTooltip open={true} x={hover.x} y={hover.y}><ChartTooltipPanel title={MODEL_TIER_LABELS[MODEL_TIERS[hover.modelId]]} rows={[{
          label: "Actual mix",
          value: `${formatScore(actualPercent, {
            decimals: 0
          })}%`
        }, {
          label: "Optimal mix",
          value: `${formatScore(optimalPercent, {
            decimals: 0
          })}%`
        }, {
          label: "Change",
          value: `${changePts >= 0 ? "+" : ""}${formatScore(changePts, {
            decimals: 0
          })} pts`,
          valueClassName: "text-muted-foreground"
        }]} /></FloatingTooltip> : null}</div>;
    $[2] = actualMix;
    $[3] = className;
    $[4] = compact;
    $[5] = hover;
    $[6] = hoveredModelId;
    $[7] = optimalMix;
    $[8] = showLabels;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
export function ModelMixLegend(t0) {
  const $ = _c(6);
  const {
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span>Model tiers:</span>;
    t3 = MODEL_ORDER.map(_temp);
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <div className={t1}>{t2}{t3}</div>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}
function _temp(modelId) {
  return <span key={modelId} className="inline-flex items-center gap-1.5"><span className="size-2 rounded-sm" style={{
      backgroundColor: MODEL_ID_COLORS[modelId]
    }} />{MODEL_TIERS[modelId]}<InfoTooltip className="p-0" content={MODEL_TIER_TOOLTIPS[MODEL_TIERS[modelId]]} label={`${MODEL_TIERS[modelId]} tier description`} /></span>;
}